/**
 * sirus/SpellBook — R232q21 Phase 3 (Sirus 1:1 UI rebuild).
 *
 * Vanilla 3.3.5a / Sirus-style class spell book: 800×550 panel that opens
 * with the P key. Replaces the legacy `Spellbook.tsx` heraldic parchment
 * with the canonical WoW grid + tab strip layout.
 *
 * Layout
 * ──────
 *   ┌──── Tabs ────────────────────────────────────────────────┐
 *   │ Общее │ Класс │ Стезя 1 │ Стезя 2 │ Стезя 3              │
 *   ├──────────────────────────────────────────┬───────────────┤
 *   │  ┌──┐┌──┐┌──┐┌──┐ (4×6 grid of 56×56)   │ Selected      │
 *   │  └──┘└──┘└──┘└──┘                       │ spell info    │
 *   │  ...                                    │ (160 px wide) │
 *   └──────────────────────────────────────────┴───────────────┘
 *
 * State sources
 *   useNet      → self level + classId
 *   useUI       → stage gate (panel only renders in 'world')
 *   CLASSES     → curated AIWARI ability list (Ability[])
 *   useDbcAbilities → optional DBC-rank lookup for trainer-rank lock icon
 *
 * Drag-drop  – payload `{ abilityId, rank, source: 'spellbook' }` written to
 *              the existing `HOTBAR_DND_MIME` mime; sirus/ActionBar already
 *              accepts the same shape.
 *
 * DEFERRED (intentional — owned by later sub-tasks):
 *   - Real trainer locator: «Тренировать у наставника» button currently emits
 *     a `aiwari-train-pulse` window event with the spell payload; the
 *     minimap-side handler that pulses the nearest trainer marker lands in
 *     Phase 4 (Minimap polish).
 *   - Drag-ghost visual + hover preview while dragging.
 *   - Multi-spec UI (the «Стезя 1/2/3» tabs are wired but content is the
 *     single curated ability list filtered by tag — full talent-tree
 *     integration arrives in Phase 4).
 *   - Long-press to bind on touch devices (currently only the open / tab
 *     swap interactions are mobile-friendly; binding stays desktop-only).
 */

import { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';

import { CLASSES, type Ability, type ClassId } from '../../data/classes.js';
import { HOTBAR_DND_MIME } from '../../state/hotbar-store.js';
import { useNet } from '../../state/net-store.js';
import { useUI } from '../../state/store.js';
import { useDbcAbilities } from '../../state/dbc-abilities-store.js';

/* ──────────────────────────────────────────────────────────────
 * Constants & helpers
 * ────────────────────────────────────────────────────────────── */

const PANEL_W = 800;
const PANEL_H = 550;
/**
 * Spell icon size — pulls the rubber-UI `--aiwari-slot-large` token
 * (clamp 40-64px). Was a literal 56 until R232q85; the new token keeps
 * it ≈ 56px at 1080p while shrinking to 40px on tiny phones and growing
 * to 64px on 4K. Stays a CSS string because every usage spreads it
 * directly into `width: / height:` style props.
 */
const ICON_PX = 'var(--aiwari-slot-large)';
const ICON_GAP = 6;
const GRID_COLS = 4;
const GRID_ROWS = 6;
const GRID_CAPACITY = GRID_COLS * GRID_ROWS;
const RIGHT_PANEL_W = 160;

type TabId = 'general' | 'class' | 'spec1' | 'spec2' | 'spec3';

interface TabSpec {
  id: TabId;
  label: string;
}

/**
 * Curated school colour palette — used for tooltip school chip + selected
 * spell border tint. Values match the brief's Russian school list.
 */
const SCHOOL_COLOURS: Record<string, { ru: string; hex: string }> = {
  arcane:    { ru: 'Тайная',     hex: '#A855F7' },
  fire:      { ru: 'Огонь',      hex: '#F97316' },
  frost:     { ru: 'Лёд',        hex: '#38BDF8' },
  nature:    { ru: 'Природа',    hex: '#22C55E' },
  shadow:    { ru: 'Тень',       hex: '#3B0764' },
  holy:      { ru: 'Свет',       hex: '#FDF6E3' },
  physical:  { ru: 'Физический', hex: '#9CA3AF' },
};

/**
 * Best-effort school inference from an Ability — the curated `Ability` shape
 * doesn't carry an explicit school field, so we infer from the icon emoji /
 * id. When uncertain we fall back to «Физический».
 */
function inferSchool(a: Ability): keyof typeof SCHOOL_COLOURS {
  const id = a.id;
  const icon = a.icon;
  if (/fire|flame|pyro|burn|ember|incendiary|firebolt|meteor/i.test(id) || /🔥|☄️/.test(icon)) return 'fire';
  if (/frost|ice|cold|chill|freeze/i.test(id) || /❄️|❄|🧊|🌊/.test(icon)) return 'frost';
  if (/arcane|polymorph|blink|mage|magic/i.test(id) || /✨|💜|🔮/.test(icon)) return 'arcane';
  if (/nature|wrath|moonfire|root|entangling|lightning|earth|wind|hurricane|leaf/i.test(id) || /⚡|🌿|🍃|🌙|⭐|🌳|🌧/.test(icon)) return 'nature';
  if (/shadow|corruption|fear|drain|curse|warlock|undead|death-coil|plague/i.test(id) || /🌑|🟣|☠️|💀|👹/.test(icon)) return 'shadow';
  if (/holy|smite|heal|blessing|divine|consecration|judgement|renew|flash|word-shield|cleanse|redemption|circle/i.test(id) || /🌟|💚|🌱|🤲|⚜️|🙏|💠|🌞/.test(icon)) return 'holy';
  return 'physical';
}

/** Resource label in Russian, by class definition's resource enum. */
const RESOURCE_LABEL: Record<string, string> = {
  mana:  'Мана',
  rage:  'Ярость',
  focus: 'Сосредоточение',
  faith: 'Вера',
  runic: 'Рун. сила',
};

/**
 * Per-class spec tab labels. WoW 3.3.5a uses three trees per class — these
 * are the canonical Russian names from Sirus's translation. When the brief's
 * fallback applies (class missing) we render placeholders like «Стезя 1/2/3».
 */
const CLASS_SPECS: Partial<Record<ClassId, [string, string, string]>> = {
  warrior:     ['Оружие',     'Ярость',       'Защита'],
  paladin:     ['Свет',       'Защита',       'Воздаяние'],
  ranger:      ['Повелитель', 'Меткость',     'Выживание'],
  rogue:       ['Ликвидация', 'Бой',          'Скрытность'],
  cleric:      ['Послушание', 'Свет',         'Тьма'],
  shaman:      ['Стихии',     'Совершенство', 'Исцеление'],
  mage:        ['Тайная',     'Огонь',        'Лёд'],
  warlock:     ['Колдовство', 'Демонология',  'Разрушение'],
  druid:       ['Баланс',     'Звериный',     'Восстановление'],
  deathknight: ['Кровь',      'Лёд',          'Нечестивость'],
};

function specLabelsFor(classId: ClassId | null): [string, string, string] {
  if (classId && CLASS_SPECS[classId]) return CLASS_SPECS[classId] as [string, string, string];
  return ['Стезя 1', 'Стезя 2', 'Стезя 3'];
}

/** Resource cost label — uses the class's resource type for the unit. */
function costLabel(a: Ability, resource: string | undefined): string {
  if (a.cost <= 0) return 'Без затрат';
  const unit = RESOURCE_LABEL[resource ?? 'mana'] ?? 'Очки';
  return `${a.cost} · ${unit}`;
}

function rangeLabel(a: Ability): string {
  if (a.range <= 0) return 'На себя';
  if (a.range <= 3) return 'Ближний бой';
  return `${a.range} м`;
}

function castLabel(a: Ability): string {
  if (a.windup <= 0) return 'Мгновенно';
  return `${a.windup.toFixed(1)} с`;
}

function cooldownLabel(a: Ability): string {
  if (a.cooldown <= 0) return 'Нет КД';
  if (a.cooldown >= 60) return `${Math.round(a.cooldown / 60)} мин`;
  return `${a.cooldown} с`;
}

/* ──────────────────────────────────────────────────────────────
 * Component
 * ────────────────────────────────────────────────────────────── */

export function SpellBook() {
  const stage = useUI((s) => s.stage);
  const selfId = useNet((s) => s.selfId);
  const players = useNet((s) => s.players);
  const dbcByClass = useDbcAbilities((s) => s.byClass);
  const ensureClassAbilities = useDbcAbilities((s) => s.ensureClassAbilities);

  const self = selfId ? players[selfId] : null;
  const classId = (self?.classId ?? null) as ClassId | null;
  const level = self?.level ?? 1;

  const cls = useMemo(
    () => (classId ? CLASSES.find((c) => c.id === classId) ?? null : null),
    [classId],
  );

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('general');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  /* P / З toggles the panel; Esc closes. Ignored when typing. */
  useEffect(() => {
    if (stage !== 'world') return;
    const onKey = (e: KeyboardEvent) => {
      const tgt = e.target as HTMLElement | null;
      if (tgt) {
        const tag = tgt.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tgt.isContentEditable) return;
      }
      const k = e.key;
      if (k === 'p' || k === 'P' || k === 'з' || k === 'З') {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (k === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stage, open]);

  /* Pull DBC ranks once we know the class — best-effort, ignored on failure. */
  useEffect(() => {
    if (classId) ensureClassAbilities(classId);
  }, [classId, ensureClassAbilities]);

  /* Build the tab → ability list mapping. The «Общее» tab shows class-agnostic
   *  starter actions (auto-attack stand-in + mobility), «Класс» the curated
   *  full ability roster, and the three spec tabs filter by ability type. */
  const tabs: TabSpec[] = useMemo(() => {
    const [s1, s2, s3] = specLabelsFor(classId);
    return [
      { id: 'general', label: 'Общее' },
      { id: 'class',   label: 'Класс' },
      { id: 'spec1',   label: s1 },
      { id: 'spec2',   label: s2 },
      { id: 'spec3',   label: s3 },
    ];
  }, [classId]);

  const abilitiesForTab = useMemo<Ability[]>(() => {
    if (!cls) return [];
    const all = cls.abilities;
    if (tab === 'class') return all;
    if (tab === 'general') {
      // Sample a handful of universal moves: melee/ranged opener + mobility.
      return all.filter((a) => a.minLevel <= 4).slice(0, GRID_CAPACITY);
    }
    // Spec tabs: split the ability roster into three rough buckets by type.
    // This is a best-effort filter until the real talent tree lands; the
    // categories overlap with the spec themes (DPS / control / support).
    const dps     = all.filter((a) => a.type === 'damage');
    const control = all.filter((a) => a.type === 'debuff' || a.type === 'mobility');
    const support = all.filter((a) => a.type === 'buff' || a.type === 'heal');
    if (tab === 'spec1') return dps;
    if (tab === 'spec2') return control;
    return support;
  }, [cls, tab]);

  /* Reset the highlighted spell when switching tabs to avoid stale selection. */
  useEffect(() => {
    setSelectedId(null);
  }, [tab]);

  if (stage !== 'world' || !open || !cls || !classId) return null;

  const dbcRows = dbcByClass[classId] ?? [];
  const dbcRankById = new Map<string, string | null>();
  for (const row of dbcRows) dbcRankById.set(row.id, row.dbcRank ?? null);

  const selected = selectedId ? cls.abilities.find((a) => a.id === selectedId) ?? null : null;
  const selectedSchool = selected ? inferSchool(selected) : null;

  /* The selected spell may have a higher trainer rank than what the player
   *  currently knows — emit a window event so the Minimap can pulse the
   *  trainer marker (handler is registered elsewhere; see deferred list). */
  const requestTrainerLocate = (a: Ability) => {
    try {
      window.dispatchEvent(
        new CustomEvent('aiwari-train-pulse', {
          detail: { abilityId: a.id, classId },
        }),
      );
    } catch {
      // CustomEvent not available (very old browsers) — fall back silently.
    }
  };

  return (
    <div
      className="aiwari-spellbook"
      data-mobile="true"
      role="dialog"
      aria-label="Книга заклинаний"
      onMouseDown={(e) => {
        // Backdrop click closes — only when the click started on the backdrop
        // itself, never bubbling up from inner content.
        if (e.target === e.currentTarget) setOpen(false);
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(2px)',
        pointerEvents: 'auto',
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", serif)',
        color: 'var(--aiwari-fg, #E8E1DA)',
      }}
    >
      <div
        className="aiwari-spellbook__panel aiwari-modal aiwari-filigree-frame"
        style={{
          position: 'relative',
          width: PANEL_W,
          maxWidth: '95vw',
          height: PANEL_H,
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: 0,
        }}
      >
        <span className="aiwari-filigree-bl" />
        <span className="aiwari-filigree-br" />
        {/* ── Header + close button ─────────────────────────────── */}
        <div
          className="aiwari-modal-title"
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            padding: '10px 16px 8px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span
              style={{
                fontFamily: 'var(--aiwari-font-caps, "Spectral SC", serif)',
                fontSize: 10,
                letterSpacing: '0.3em',
                color: 'var(--aiwari-fg-muted, #B19B7E)',
                textTransform: 'uppercase',
              }}
            >
              Книга заклинаний
            </span>
            <span
              style={{
                fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
                fontSize: 22,
                color: cls.colour,
                lineHeight: 1.1,
              }}
            >
              {cls.name.ru}
              <span
                style={{
                  marginLeft: 12,
                  fontSize: 13,
                  color: 'var(--aiwari-fg-muted, #B19B7E)',
                  fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
                }}
              >
                · уровень {level}
              </span>
            </span>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Закрыть"
            style={{
              width: 28,
              height: 28,
              padding: 0,
              fontSize: 18,
              lineHeight: '24px',
              color: 'var(--aiwari-fg-muted, #B19B7E)',
              background: 'transparent',
              border: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
              borderRadius: 2,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>

        {/* ── Tab strip ─────────────────────────────────────────── */}
        <div
          className="aiwari-spellbook__tabs"
          style={{
            display: 'flex',
            gap: 2,
            padding: '6px 12px 0',
            borderBottom: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
          }}
        >
          {tabs.map((t) => {
            const active = t.id === tab;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                style={{
                  padding: '6px 14px',
                  fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
                  fontSize: 12,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: active ? cls.colour : 'var(--aiwari-fg-muted, #B19B7E)',
                  background: active
                    ? 'linear-gradient(180deg, rgba(0,0,0,0.0), rgba(0,0,0,0.55))'
                    : 'transparent',
                  border: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
                  borderBottom: active ? '2px solid ' + cls.colour : '1px solid transparent',
                  borderRadius: '3px 3px 0 0',
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* ── Body: grid + right info panel ─────────────────────── */}
        <div
          className="aiwari-spellbook__body"
          style={{
            flex: 1,
            display: 'flex',
            minHeight: 0,
            background: `
              linear-gradient(90deg,
                transparent 0%,
                hsl(0 0% 0% / 0.45) 49.5%,
                hsl(0 0% 0% / 0.65) 50%,
                hsl(0 0% 0% / 0.45) 50.5%,
                transparent 100%),
              linear-gradient(135deg, #E8DCC0 0%, #D6C19B 45%, #C4B391 100%)`,
          }}
        >
          {/* Spell grid — left page */}
          <div
            className="aiwari-spellbook__grid-wrap"
            style={{
              flex: 1,
              padding: 16,
              overflowY: 'auto',
              borderRight: '1px solid hsl(44 60% 18%)',
              boxShadow: 'inset 0 0 28px hsl(36 35% 35% / 0.45)',
            }}
          >
            <div
              className="aiwari-spellbook__grid"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${GRID_COLS}, ${ICON_PX})`,
                gridAutoRows: ICON_PX,
                gap: ICON_GAP,
                justifyContent: 'start',
              }}
            >
              {Array.from({ length: GRID_CAPACITY }, (_, idx) => {
                const a = abilitiesForTab[idx] ?? null;
                if (!a) return <EmptySlot key={`empty-${idx}`} />;
                const known = a.minLevel <= level;
                const dbcRank = dbcRankById.get(a.id) ?? null;
                // Trainer-rank lock: in the curated client list every ability
                // has a single rank, so we infer "needs train" when the DBC
                // catalogue carries a higher rank string than rank 1. Any
                // non-null rank-2+ string flips the lock on; once the player
                // visits a trainer the server adds higher rows and this
                // resolves. Best-effort heuristic — Phase 4 wires real ranks.
                const trainerHigher = !!dbcRank && /[2-9]|10/.test(dbcRank);
                return (
                  <SpellIcon
                    key={a.id}
                    ability={a}
                    classColour={cls.colour}
                    known={known}
                    trainerHigher={trainerHigher && known}
                    selected={selectedId === a.id}
                    onSelect={() => setSelectedId(a.id)}
                  />
                );
              })}
            </div>
          </div>

          {/* Right info panel — right page */}
          <aside
            className="aiwari-spellbook__info"
            style={{
              width: RIGHT_PANEL_W,
              flexShrink: 0,
              padding: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
              background: 'transparent',
              boxShadow: 'inset 0 0 28px hsl(36 35% 35% / 0.45)',
              overflowY: 'auto',
              color: '#2A1F12',
            }}
          >
            {selected ? (
              <SelectedPanel
                ability={selected}
                school={selectedSchool ?? 'physical'}
                resource={cls.resource}
                classColour={cls.colour}
                trainerHigher={(() => {
                  const r = dbcRankById.get(selected.id) ?? null;
                  return !!r && /[2-9]|10/.test(r);
                })()}
                onTrain={() => requestTrainerLocate(selected)}
              />
            ) : (
              <EmptyInfo />
            )}
          </aside>
        </div>

        {/* ── Footer hint ───────────────────────────────────────── */}
        <div
          style={{
            padding: '6px 16px',
            borderTop: '1px solid var(--aiwari-frame-edge, hsl(44 30% 25%))',
            fontSize: 10,
            fontFamily: 'var(--aiwari-font-caps, "Spectral SC", serif)',
            letterSpacing: '0.15em',
            color: 'var(--aiwari-fg-muted, #B19B7E)',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>P · З — открыть/закрыть · перетащи на панель действий</span>
          <span>{abilitiesForTab.length} заклинаний</span>
        </div>
      </div>

      {/* Mobile overrides — full-screen sheet, drag-drop hidden via CSS. */}
      <style>{`
        @media (pointer: coarse) {
          .aiwari-spellbook[data-mobile='true'] .aiwari-spellbook__panel {
            width: 100vw !important;
            height: 100vh !important;
            max-width: 100vw !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
          }
          .aiwari-spellbook[data-mobile='true'] .aiwari-spellbook__body {
            flex-direction: column !important;
          }
          .aiwari-spellbook[data-mobile='true'] .aiwari-spellbook__grid-wrap {
            border-right: none !important;
            border-bottom: 1px solid var(--aiwari-frame-edge, hsl(44 30% 25%));
          }
          .aiwari-spellbook[data-mobile='true'] .aiwari-spellbook__info {
            width: 100% !important;
          }
        }
      `}</style>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────
 * Sub-components
 * ────────────────────────────────────────────────────────────── */

function EmptySlot() {
  return (
    <div
      aria-hidden
      style={{
        width: ICON_PX,
        height: ICON_PX,
        background: 'var(--aiwari-frame-stone, #1F1A14)',
        border: '1px dashed var(--aiwari-frame-edge, hsl(44 30% 25%))',
        borderRadius: 2,
        opacity: 0.45,
      }}
    />
  );
}

interface SpellIconProps {
  ability: Ability;
  classColour: string;
  known: boolean;
  trainerHigher: boolean;
  selected: boolean;
  onSelect: () => void;
}

function SpellIcon({
  ability,
  classColour,
  known,
  trainerHigher,
  selected,
  onSelect,
}: SpellIconProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [hover, setHover] = useState(false);

  const slotStyle: CSSProperties = {
    position: 'relative',
    width: ICON_PX,
    height: ICON_PX,
    padding: 0,
    background: 'var(--aiwari-frame-stone, #1F1A14)',
    border: `2px solid ${selected ? classColour : 'var(--aiwari-frame-edge, hsl(44 30% 25%))'}`,
    borderRadius: 2,
    cursor: known ? 'grab' : 'not-allowed',
    boxSizing: 'border-box',
    overflow: 'visible',
    filter: known ? undefined : 'grayscale(0.95)',
    opacity: known ? 1 : 0.5,
    boxShadow: selected
      ? `0 0 8px ${classColour}, inset 0 0 6px ${classColour}55`
      : 'inset 0 1px 0 hsl(0 0% 100% / 0.08), 0 1px 2px hsl(0 0% 0% / 0.4)',
  };

  const handleDragStart = (e: React.DragEvent<HTMLButtonElement>) => {
    if (!known) {
      e.preventDefault();
      return;
    }
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(
      HOTBAR_DND_MIME,
      JSON.stringify({ abilityId: ability.id, rank: 1, source: 'spellbook' }),
    );
  };

  return (
    <button
      type="button"
      className="aiwari-spellbook__icon"
      style={slotStyle}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      draggable={known}
      onDragStart={handleDragStart}
      aria-label={ability.name.ru}
    >
      {/* Icon — try PNG, fall back to coloured circle with first letter. */}
      {imgFailed ? (
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 4,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${classColour}55, ${classColour}22)`,
            color: classColour,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
            fontSize: 22,
            fontWeight: 700,
          }}
        >
          {ability.name.ru.slice(0, 1).toUpperCase()}
        </span>
      ) : (
        <img
          src={`${import.meta.env.BASE_URL}icons/abilities/${ability.id}.png`}
          alt=""
          onError={() => setImgFailed(true)}
          style={{
            position: 'absolute',
            inset: 2,
            width: 'calc(100% - 4px)',
            height: 'calc(100% - 4px)',
            objectFit: 'cover',
            borderRadius: 1,
            pointerEvents: 'none',
          }}
        />
      )}

      {/* Trainer-rank lock overlay (chain icon, bottom-left). */}
      {trainerHigher && (
        <span
          aria-hidden
          title="У наставника есть более высокий ранг"
          style={{
            position: 'absolute',
            left: 1,
            top: 1,
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.7)',
            border: '1px solid var(--aiwari-gold-soft, #FFD479)',
            borderRadius: 2,
            color: 'var(--aiwari-gold-soft, #FFD479)',
            fontSize: 10,
            lineHeight: 1,
          }}
        >
          ⛓
        </span>
      )}

      {/* Rank label, bottom-right. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: 2,
          bottom: 1,
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 9,
          lineHeight: 1,
          color: 'var(--aiwari-gold-soft, #FFD479)',
          textShadow: '0 1px 0 #000',
          background: 'rgba(0,0,0,0.55)',
          padding: '1px 3px',
          borderRadius: 2,
          pointerEvents: 'none',
        }}
      >
        Ранг 1
      </span>

      {/* Hover tooltip — pops out to the right of the icon. */}
      {hover && (
        <SpellTooltip ability={ability} known={known} />
      )}
    </button>
  );
}

function SpellTooltip({ ability, known }: { ability: Ability; known: boolean }) {
  const school = inferSchool(ability);
  const schoolMeta = SCHOOL_COLOURS[school];
  return (
    <div
      role="tooltip"
      style={{
        position: 'absolute',
        top: 0,
        left: '100%',
        marginLeft: 8,
        zIndex: 50,
        width: 240,
        padding: 10,
        background: 'var(--aiwari-bg-card-solid, #14100C)',
        border: `1px solid ${schoolMeta.hex}`,
        borderRadius: 3,
        boxShadow: '0 6px 24px rgba(0,0,0,0.7)',
        color: 'var(--aiwari-fg, #E8E1DA)',
        pointerEvents: 'none',
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", serif)',
        fontSize: 12,
        lineHeight: 1.4,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
          fontSize: 14,
          color: schoolMeta.hex,
          marginBottom: 2,
        }}
      >
        {ability.name.ru}
      </div>
      <div
        style={{
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 10,
          color: 'var(--aiwari-fg-muted, #B19B7E)',
          marginBottom: 4,
        }}
      >
        Ранг 1 · ур. {ability.minLevel} · школа: {schoolMeta.ru}
      </div>
      <div
        style={{
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 10,
          color: 'var(--aiwari-fg-muted, #B19B7E)',
          marginBottom: 6,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 2,
        }}
      >
        <span>Каст: {castLabel(ability)}</span>
        <span>КД: {cooldownLabel(ability)}</span>
        <span>Дист.: {rangeLabel(ability)}</span>
        <span>Затраты: {ability.cost > 0 ? ability.cost : '—'}</span>
      </div>
      <div style={{ fontStyle: 'italic', color: '#C9B68A' }}>
        {ability.desc.ru}
      </div>
      {!known && (
        <div
          style={{
            marginTop: 6,
            paddingTop: 4,
            borderTop: '1px solid rgba(255,255,255,0.12)',
            color: 'var(--aiwari-quality-poor, #999)',
            fontSize: 10,
          }}
        >
          Не изучено — нужен уровень {ability.minLevel}.
        </div>
      )}
    </div>
  );
}

interface SelectedPanelProps {
  ability: Ability;
  school: keyof typeof SCHOOL_COLOURS;
  resource: string;
  classColour: string;
  trainerHigher: boolean;
  onTrain: () => void;
}

function SelectedPanel({
  ability,
  school,
  resource,
  classColour,
  trainerHigher,
  onTrain,
}: SelectedPanelProps) {
  const meta = SCHOOL_COLOURS[school];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div
          style={{
            width: 48,
            height: 48,
            border: `2px solid ${classColour}`,
            borderRadius: 3,
            background: `radial-gradient(circle, ${meta.hex}33, ${meta.hex}11)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 26,
            flexShrink: 0,
          }}
        >
          {ability.icon}
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
              fontSize: 14,
              color: meta.hex,
              lineHeight: 1.15,
              wordBreak: 'break-word',
            }}
          >
            {ability.name.ru}
          </div>
          <div
            style={{
              fontFamily: 'var(--aiwari-font-caps, "Spectral SC", serif)',
              fontSize: 9,
              letterSpacing: '0.2em',
              color: '#5A4225',
              textTransform: 'uppercase',
              marginTop: 1,
            }}
          >
            Ранг 1 · ур. {ability.minLevel}
          </div>
        </div>
      </div>

      <div
        style={{
          fontFamily: 'var(--aiwari-font-body, "EB Garamond", serif)',
          fontSize: 12,
          fontStyle: 'italic',
          color: '#3A2710',
          lineHeight: 1.4,
        }}
      >
        {ability.desc.ru}
      </div>

      <dl
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: '3px 8px',
          margin: 0,
          fontFamily: 'var(--aiwari-font-mono, "JetBrains Mono", monospace)',
          fontSize: 10,
          color: '#5A4225',
        }}
      >
        <dt>Школа:</dt>
        <dd style={{ margin: 0, color: meta.hex }}>{meta.ru}</dd>
        <dt>Затраты:</dt>
        <dd style={{ margin: 0, color: '#2A1F12' }}>{costLabel(ability, resource)}</dd>
        <dt>Дальность:</dt>
        <dd style={{ margin: 0, color: '#2A1F12' }}>{rangeLabel(ability)}</dd>
        <dt>Время каста:</dt>
        <dd style={{ margin: 0, color: '#2A1F12' }}>{castLabel(ability)}</dd>
        <dt>Перезарядка:</dt>
        <dd style={{ margin: 0, color: '#2A1F12' }}>{cooldownLabel(ability)}</dd>
      </dl>

      {trainerHigher && (
        <button
          type="button"
          onClick={onTrain}
          style={{
            marginTop: 4,
            padding: '8px 6px',
            background: 'linear-gradient(180deg, var(--aiwari-gold-soft, #FFD479), var(--aiwari-gold, #FAB300))',
            color: '#1A140E',
            border: '1px solid var(--aiwari-gold-dark, hsl(44 100% 30%))',
            borderRadius: 3,
            fontFamily: 'var(--aiwari-font-display, "Cinzel", serif)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            cursor: 'pointer',
            boxShadow: '0 1px 0 rgba(255,255,255,0.25) inset, 0 2px 6px rgba(0,0,0,0.5)',
          }}
        >
          Тренировать у наставника
        </button>
      )}
    </div>
  );
}

function EmptyInfo() {
  return (
    <div
      style={{
        margin: 'auto 0',
        textAlign: 'center',
        fontStyle: 'italic',
        fontSize: 12,
        color: '#5A4225',
        opacity: 0.85,
        fontFamily: 'var(--aiwari-font-body, "EB Garamond", serif)',
      }}
    >
      Выбери заклинание из сетки слева — здесь покажется его описание и параметры.
    </div>
  );
}

export default SpellBook;
